import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Building2,
  MapPin,
  CheckCircle,
  AlertCircle,
  Image as ImageIcon,
  Briefcase,
  X,
  User,
  Mail
} from 'lucide-react';

interface Assignment {
  id: string;
  project_name: string;
  client_name: string;
  location: string;
  start_date: string;
  end_date: string;
  status: 'active' | 'upcoming' | 'completed';
  hours_logged: number;
  total_hours: number;
  description: string;
  client_contact: string;
  client_contact_email: string;
  manager_name: string;
  manager_email: string;
}

export default function Assignments() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { theme, setTheme, setThemeTransient } = useTheme();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoadingLogo, setIsLoadingLogo] = useState(true);
  const [activeTab, setActiveTab] = useState<'active' | 'upcoming' | 'completed'>('active');
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setThemeTransient('light');
  }, [setThemeTransient]);

  useEffect(() => {
    if (user) {
      fetchAssignments();
    }
    fetchLogo();
  }, [user?.id]);

  const fetchLogo = async () => {
    try {
      setIsLoadingLogo(true);
      const defaultLogo = 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format';
      setLogoUrl(defaultLogo);

      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'logo_url')
        .maybeSingle();

      if (!error && data?.value) {
        const img = new Image();
        img.onload = () => {
          setLogoUrl(data.value);
          setIsLoadingLogo(false);
        };
        img.onerror = () => setIsLoadingLogo(false);
        img.src = data.value;
      } else {
        setIsLoadingLogo(false);
      }
    } catch (err) {
      setIsLoadingLogo(false);
    }
  };

  const fetchAssignments = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('consultant_assignments')
        .select(`
          *,
          workday_client_profiles!consultant_assignments_client_id_fkey (
            full_name,
            email
          ),
          companies (
            name
          )
        `)
        .eq('consultant_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedAssignments: Assignment[] = (data || []).map(assignment => ({
        id: assignment.id,
        project_name: assignment.project_name || 'Workday Project',
        client_name: assignment.companies?.name || 'Client',
        location: assignment.location || 'Remote',
        start_date: assignment.start_date,
        end_date: assignment.end_date,
        status: assignment.status as 'active' | 'upcoming' | 'completed',
        hours_logged: assignment.hours_logged || 0,
        total_hours: assignment.total_hours || 0,
        description: assignment.description || '',
        client_contact: assignment.workday_client_profiles?.full_name || 'N/A',
        client_contact_email: assignment.workday_client_profiles?.email || '',
        manager_name: assignment.manager_name || 'Perplexity Manager',
        manager_email: assignment.manager_email || 'support@perplexityalm.com'
      }));

      setAssignments(formattedAssignments);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAssignments = assignments.filter(a => a.status === activeTab);

  const getStatusBadge = (status: string) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      upcoming: 'bg-blue-100 text-blue-800',
      completed: 'bg-gray-100 text-gray-800'
    };
    return badges[status as keyof typeof badges] || badges.active;
  };

  const getProgressPercentage = (logged: number, total: number) => {
    return Math.round((logged / total) * 100);
  };

  return (
    <div className={`min-h-screen transition-colors duration-200 ${
      theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
    }`}>
      {/* Header */}
      <nav className="fixed w-full z-50 bg-black backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="text-white -mt-4 -mb-4 cursor-default select-none">
              {isLoadingLogo ? (
                <div className="h-32 md:h-28 w-[200px] flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : logoUrl ? (
                <img
                  src="/1.png"
                  alt="Perplexity"
                  className="h-40 w-auto object-contain"
                />
              ) : (
                <ImageIcon size={32} className="text-primary" />
              )}
            </div>
            <button
              onClick={() => navigate('/consultant/dashboard')}
              className="text-white hover:text-primary transition-colors flex items-center"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-4">Your Assignments</h1>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Manage your current and upcoming project assignments
            </p>
          </div>

          {/* Tabs */}
          <div className="mb-6 flex space-x-2">
            {(['active', 'upcoming', 'completed'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                  activeTab === tab
                    ? 'bg-primary text-black'
                    : theme === 'dark'
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Assignments List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : filteredAssignments.length === 0 ? (
            <div className={`text-center py-12 rounded-xl ${
              theme === 'dark' ? 'bg-gray-800' : 'bg-white'
            }`}>
              <Briefcase className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-xl font-semibold mb-2">No {activeTab} assignments</h3>
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                {activeTab === 'active' && 'You don\'t have any active assignments at the moment'}
                {activeTab === 'upcoming' && 'No upcoming assignments scheduled'}
                {activeTab === 'completed' && 'No completed assignments yet'}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredAssignments.map((assignment) => {
                const progress = getProgressPercentage(assignment.hours_logged, assignment.total_hours);

                return (
                  <div
                    key={assignment.id}
                    className={`p-6 rounded-xl transition-all duration-200 ${
                      theme === 'dark'
                        ? 'bg-gray-800 border border-gray-700'
                        : 'bg-white border border-gray-200 shadow-sm hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-2xl font-bold">{assignment.project_name}</h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusBadge(assignment.status)}`}>
                            {assignment.status.charAt(0).toUpperCase() + assignment.status.slice(1)}
                          </span>
                        </div>
                        <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                          {assignment.description}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      <div className="flex items-center space-x-3">
                        <Building2 className="w-5 h-5 text-primary" />
                        <div>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Client</p>
                          <p className="font-semibold">{assignment.client_name}</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <MapPin className="w-5 h-5 text-primary" />
                        <div>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Location</p>
                          <p className="font-semibold">{assignment.location}</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <Calendar className="w-5 h-5 text-primary" />
                        <div>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Start Date</p>
                          <p className="font-semibold">{new Date(assignment.start_date).toLocaleDateString()}</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <Calendar className="w-5 h-5 text-primary" />
                        <div>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>End Date</p>
                          <p className="font-semibold">{new Date(assignment.end_date).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>

                    {assignment.status === 'active' && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <Clock className="w-5 h-5 text-primary" />
                            <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                              Hours Progress
                            </span>
                          </div>
                          <span className="font-semibold">
                            {assignment.hours_logged} / {assignment.total_hours} hours ({progress}%)
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    <div className="mt-6 flex space-x-4">
                      <button
                        onClick={() => setSelectedAssignment(assignment)}
                        className="flex-1 bg-primary text-black font-semibold py-2 rounded-lg hover:bg-primary/90 transition-colors"
                      >
                        View Details
                      </button>
                      {assignment.status === 'active' && (
                        <button
                          onClick={() => navigate('/consultant/timesheet')}
                          className={`flex-1 font-semibold py-2 rounded-lg transition-colors ${
                            theme === 'dark'
                              ? 'bg-gray-700 text-white hover:bg-gray-600'
                              : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                          }`}
                        >
                          Log Time
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Assignment Details Modal */}
      {selectedAssignment && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className={`max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-xl ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}>
            {/* Modal Header */}
            <div className={`sticky top-0 flex items-center justify-between p-6 border-b ${
              theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <h2 className="text-2xl font-bold">Assignment Details</h2>
              <button
                onClick={() => setSelectedAssignment(null)}
                className={`p-2 rounded-lg transition-colors ${
                  theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Project Name and Status */}
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-3xl font-bold">{selectedAssignment.project_name}</h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusBadge(selectedAssignment.status)}`}>
                    {selectedAssignment.status.charAt(0).toUpperCase() + selectedAssignment.status.slice(1)}
                  </span>
                </div>
                <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  {selectedAssignment.description}
                </p>
              </div>

              {/* Project Details Grid */}
              <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 p-6 rounded-lg ${
                theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
              }`}>
                <div className="flex items-start space-x-3">
                  <Building2 className="w-6 h-6 text-primary mt-1" />
                  <div>
                    <p className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Client Name
                    </p>
                    <p className="text-lg font-semibold">{selectedAssignment.client_name}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <MapPin className="w-6 h-6 text-primary mt-1" />
                  <div>
                    <p className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Location
                    </p>
                    <p className="text-lg font-semibold">{selectedAssignment.location}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Calendar className="w-6 h-6 text-primary mt-1" />
                  <div>
                    <p className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Start Date
                    </p>
                    <p className="text-lg font-semibold">
                      {new Date(selectedAssignment.start_date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Calendar className="w-6 h-6 text-primary mt-1" />
                  <div>
                    <p className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      End Date
                    </p>
                    <p className="text-lg font-semibold">
                      {new Date(selectedAssignment.end_date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                {selectedAssignment.status === 'active' && (
                  <>
                    <div className="flex items-start space-x-3">
                      <Clock className="w-6 h-6 text-primary mt-1" />
                      <div>
                        <p className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Hours Logged
                        </p>
                        <p className="text-lg font-semibold">{selectedAssignment.hours_logged} hours</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Clock className="w-6 h-6 text-primary mt-1" />
                      <div>
                        <p className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Total Hours
                        </p>
                        <p className="text-lg font-semibold">{selectedAssignment.total_hours} hours</p>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Contact Information */}
              <div>
                <h4 className="text-xl font-bold mb-4">Contact Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Client Point of Contact */}
                  <div className={`p-5 rounded-lg ${
                    theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                  }`}>
                    <p className={`text-sm font-medium mb-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Client Point of Contact
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <User className="w-5 h-5 text-primary flex-shrink-0" />
                        <p className="font-semibold">{selectedAssignment.client_contact}</p>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Mail className="w-5 h-5 text-primary flex-shrink-0" />
                        <a
                          href={`mailto:${selectedAssignment.client_contact_email}`}
                          className="text-primary hover:underline break-all"
                        >
                          {selectedAssignment.client_contact_email}
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Reporting Manager */}
                  <div className={`p-5 rounded-lg ${
                    theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                  }`}>
                    <p className={`text-sm font-medium mb-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Reporting Manager
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <User className="w-5 h-5 text-primary flex-shrink-0" />
                        <p className="font-semibold">{selectedAssignment.manager_name}</p>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Mail className="w-5 h-5 text-primary flex-shrink-0" />
                        <a
                          href={`mailto:${selectedAssignment.manager_email}`}
                          className="text-primary hover:underline break-all"
                        >
                          {selectedAssignment.manager_email}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress Section for Active Assignments */}
              {selectedAssignment.status === 'active' && (
                <div className={`p-6 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                }`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-lg">Project Progress</h4>
                    <span className="text-2xl font-bold text-primary">
                      {getProgressPercentage(selectedAssignment.hours_logged, selectedAssignment.total_hours)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-primary h-3 rounded-full transition-all duration-300"
                      style={{ width: `${getProgressPercentage(selectedAssignment.hours_logged, selectedAssignment.total_hours)}%` }}
                    ></div>
                  </div>
                  <p className={`mt-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {selectedAssignment.total_hours - selectedAssignment.hours_logged} hours remaining
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-4 pt-4">
                {selectedAssignment.status === 'active' && (
                  <button
                    onClick={() => {
                      setSelectedAssignment(null);
                      navigate('/consultant/timesheet');
                    }}
                    className="flex-1 bg-primary text-black font-semibold py-3 rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Log Time for This Project
                  </button>
                )}
                <button
                  onClick={() => setSelectedAssignment(null)}
                  className={`flex-1 font-semibold py-3 rounded-lg transition-colors ${
                    theme === 'dark'
                      ? 'bg-gray-700 text-white hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
